import { useState, useEffect, useMemo } from "react";
import { useTranslate } from "react-admin";
import {
  Box,
  Card,
  CardContent,
  Typography,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import {
  getTextilesPurchases,
  getTextilesCost,
  type TTextilePurchase,
} from "@/api/customs/textiles";
import { KAMGroupedTable } from "./KAMGroupedTable";
import { DetailTable } from "./DetailTable";
import { PaymentSearchTable } from "./PaymentSearchTable";
import { CostoPlatamTable } from "./CostoPlatamTable";
import { PaymentMethodPivotTable } from "./PaymentMethodPivotTable";
import { DesembolsosPlatamTable } from "./DesembolsosPlatamTable";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`textiles-tabpanel-${index}`}
      aria-labelledby={`textiles-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

export const DashboardTextiles = () => {
  const translate = useTranslate();
  const getAvailableMonths = useMemo(() => {
    const months: string[] = [];
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // 0-indexed

    // Add months from 2025-01 onwards
    for (let year = 2025; year <= currentYear; year++) {
      const startMonth = year === 2025 ? 1 : 1;
      const endMonth = year === currentYear ? currentMonth : 12;
      
      for (let month = startMonth; month <= endMonth; month++) {
        const monthStr = `${year}-${String(month).padStart(2, "0")}`;
        months.push(monthStr);
      }
    }

    // Sort from most recent to oldest
    return months.sort().reverse();
  }, []);

  const getAvailableMonthsCostoPlatam = useMemo(() => {
    const months: string[] = [];
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // 0-indexed

    // Add months from 2025-12 onwards
    for (let year = 2025; year <= currentYear; year++) {
      const startMonth = year === 2025 ? 12 : 1;
      const endMonth = year === currentYear ? currentMonth : 12;
      
      for (let month = startMonth; month <= endMonth; month++) {
        const monthStr = `${year}-${String(month).padStart(2, "0")}`;
        months.push(monthStr);
      }
    }

    // Sort from most recent to oldest
    return months.sort().reverse();
  }, []);

  const [selectedMonth, setSelectedMonth] = useState<string>(
    getAvailableMonths[0] || "2025-01"
  );
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TTextilePurchase[]>([]);
  const [costByMonth, setCostByMonth] = useState<Record<string, number>>({});

  const fetchData = async () => {
    if (!selectedMonth) {
      setError("Por favor selecciona un mes");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [response, costData] = await Promise.all([
        getTextilesPurchases({ month: selectedMonth }),
        getTextilesCost({ con_iva: true }),
      ]);
      setData(response);
      setCostByMonth(costData);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Error al cargar los datos"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Si cambiamos a la pestaña Costo Platam (index 3) y el mes actual no es válido
    if (newValue === 3) {
      if (!getAvailableMonthsCostoPlatam.includes(selectedMonth)) {
        setSelectedMonth(getAvailableMonthsCostoPlatam[0] || "2025-12");
      }
    } else if (newValue !== 5) {
      // Si salimos de Costo Platam y Desembolsos (que no usa mes) y el mes no es válido para otras pestañas
      if (!getAvailableMonths.includes(selectedMonth)) {
        setSelectedMonth(getAvailableMonths[0] || "2025-01");
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard Textiles
      </Typography>

      {/* Filtro de mes */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            select
            label="Mes"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            fullWidth
            sx={{ maxWidth: 300 }}
          >
            {(tabValue === 3 ? getAvailableMonthsCostoPlatam : getAvailableMonths).map((month) => (
              <MenuItem key={month} value={month}>
                {month}
              </MenuItem>
            ))}
          </TextField>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={translate("resources.textiles.tabs.resumen_kam")} />
            <Tab label={translate("resources.textiles.tabs.detalle_pedidos")} />
            <Tab label={translate("resources.textiles.tabs.buscar_pago")} />
            <Tab label={translate("resources.textiles.tabs.costo_platam")} />
            <Tab label={translate("resources.textiles.tabs.pivot_metodo_pago")} />
            <Tab label={translate("resources.textiles.tabs.desembolsos_platam")} />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            )}
            {!loading && data.length > 0 && (
              <KAMGroupedTable data={data} totalCost={costByMonth[selectedMonth] ?? null} />
            )}
            {!loading && data.length === 0 && !error && (
              <Alert severity="info">
                No hay datos disponibles para el mes seleccionado
              </Alert>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            )}
            {!loading && data.length > 0 && <DetailTable data={data} />}
            {!loading && data.length === 0 && !error && (
              <Alert severity="info">
                No hay datos disponibles para el mes seleccionado
              </Alert>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <PaymentSearchTable />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <CostoPlatamTable month={selectedMonth} />
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            )}
            {!loading && data.length > 0 && (
              <PaymentMethodPivotTable data={data} />
            )}
            {!loading && data.length === 0 && !error && (
              <Alert severity="info">
                No hay datos disponibles para el mes seleccionado
              </Alert>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            <DesembolsosPlatamTable />
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
};

